import React, { useEffect, useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Settings, Maximize2, Download, ZoomIn, ZoomOut, GripVertical, LayoutDashboard, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchWeatherForecast } from '../services/api';
import { MarketZone } from '../types/data';
import { VariableType } from '../hooks/useDashboardState';
import { Dashboard, SavedChart } from './views/DashboardsView';
const ZONE_MAP: Record<string, MarketZone> = {
  Germany: 'DE',
  Spain: 'ES',
  UK: 'UK',
  France: 'FR'
};
interface ChartsPanelProps {
  activeRegion?: string;
  activeZone?: {
    name: string;
    code: string;
  };
  activeVariable?: VariableType;
  // Optional context for when adding to dashboard
  dashboardContext?: {
    defaultTitle?: string;
  };
}
const CustomTooltip = ({
  active,
  payload,
  label,
  unit
}: any) => {
  if (active && payload && payload.length) {
    // Get the full timestamp from the first payload item
    const dataPoint = payload[0]?.payload;
    const timestamp = dataPoint?.timestamp;
    // Format date and time
    const dateStr = timestamp ? new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : '';
    const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) : label;
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-50 rounded-xl p-4 shadow-2xl" data-id="element-47">
        <div className="mb-3 pb-2 border-b border-slate-200" data-id="element-48">
          <p className="text-xs font-semibold text-slate-900" data-id="element-49">{dateStr}</p>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-50">{timeStr}</p>
        </div>
        <div className="space-y-2" data-id="element-51">
          {payload.map((entry: any) => {
          if (entry.name === 'upperBound' || entry.name === 'lowerBound' || entry.name === 'Std Dev') return null;
          return <div key={entry.name} className="flex items-center justify-between gap-6" data-id="element-52">
                <div className="flex items-center gap-2" data-id="element-53">
                  <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{
                backgroundColor: entry.color
              }} data-id="element-54" />
                  <span className="text-xs font-medium text-slate-700" data-id="element-55">
                    {entry.name}
                  </span>
                </div>
                <span className="text-sm font-bold text-slate-900 font-mono" data-id="element-56">
                  {entry.value.toFixed(1)}
                </span>
              </div>;
        })}
        </div>
      </div>;
  }
  return null;
};
export function ChartsPanel({
  activeRegion = 'Germany',
  activeZone,
  activeVariable = 'Wind',
  dashboardContext
}: ChartsPanelProps) {
  const [mode, setMode] = useState<'Data' | 'Delta'>('Data');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  // MOVED: Define getVariableConfig BEFORE using it in useMemo
  const getVariableConfig = () => {
    switch (activeVariable) {
      case 'Wind':
        return {
          title: 'Wind Speed Forecast',
          unit: 'm/s',
          yAxisLabel: 'WIND SPEED (m/s)',
          accessor: 'windSpeed'
        };
      case 'Solar':
        return {
          title: 'Solar Irradiance Forecast',
          unit: 'W/m²',
          yAxisLabel: 'SOLAR IRRADIANCE (W/m²)',
          accessor: 'solarRadiation'
        };
      case 'Temperature':
        return {
          title: 'Temperature Forecast',
          unit: '°C',
          yAxisLabel: 'TEMPERATURE (°C)',
          accessor: 'temperature'
        };
    }
  };
  // Memoize the config to prevent unnecessary re-renders
  const config = useMemo(() => getVariableConfig(), [activeVariable]);
  // Dashboard adding state
  const [showDashboardMenu, setShowDashboardMenu] = useState(false);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [newDashboardTitle, setNewDashboardTitle] = useState('');
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Create a stable key for the current chart configuration
  const chartKey = useMemo(() => {
    const zone = activeZone?.code || ZONE_MAP[activeRegion] || 'DE';
    return `${zone}-${activeVariable}`;
  }, [activeRegion, activeZone?.code, activeVariable]);
  useEffect(() => {
    loadForecastData();
  }, [chartKey]); // Only reload when the stable key changes
  // Load dashboards when menu opens
  useEffect(() => {
    if (showDashboardMenu) {
      const saved = localStorage.getItem('jua-dashboards');
      if (saved) {
        try {
          setDashboards(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to load dashboards', e);
        }
      }
      // Pre-fill title if context provided
      if (dashboardContext?.defaultTitle) {
        setNewDashboardTitle(dashboardContext.defaultTitle);
        // If no dashboards exist or we have a specific title context, default to creating mode
        if (!saved || JSON.parse(saved).length === 0) {
          setIsCreatingDashboard(true);
        }
      }
    }
  }, [showDashboardMenu, dashboardContext]);
  const loadForecastData = async () => {
    setLoading(true);
    const zone = activeZone?.code as MarketZone || ZONE_MAP[activeRegion] || 'DE';
    const forecasts = await fetchWeatherForecast(zone);
    if (forecasts.length === 0) {
      setLoading(false);
      return;
    }
    const timePoints = [...new Set(forecasts.map(f => f.timestamp.toISOString()))];
    const data = timePoints.slice(0, 48).map(time => {
      const timeForecasts = forecasts.filter(f => f.timestamp.toISOString() === time);
      const timestamp = new Date(time);
      const ept2 = timeForecasts.find(f => f.model === 'EPT-2');
      const ecifs = timeForecasts.find(f => f.model === 'EC-IFS');
      const gfs = timeForecasts.find(f => f.model === 'GFS');
      // Get values based on active variable
      const values = [ept2?.parameters[config.accessor] || 0, ecifs?.parameters[config.accessor] || 0, gfs?.parameters[config.accessor] || 0].filter(v => v > 0);
      const mean = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      const stdDev = values.length > 1 ? Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length) : 0;
      return {
        time: timestamp.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        fullTime: time,
        timestamp: timestamp,
        hour: timestamp.getHours(),
        date: timestamp.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }),
        isNewDay: timestamp.getHours() === 0,
        ept2: ept2?.parameters[config.accessor] || null,
        ecifs: ecifs?.parameters[config.accessor] || null,
        gfs: gfs?.parameters[config.accessor] || null,
        mean,
        stdDev,
        upperBound: mean + stdDev,
        lowerBound: Math.max(0, mean - stdDev),
        // Delta values (difference from mean)
        ept2Delta: ept2?.parameters[config.accessor] ? ept2.parameters[config.accessor] - mean : null,
        ecifsDelta: ecifs?.parameters[config.accessor] ? ecifs.parameters[config.accessor] - mean : null,
        gfsDelta: gfs?.parameters[config.accessor] ? gfs.parameters[config.accessor] - mean : null
      };
    });
    setChartData(data);
    setLoading(false);
  };
  const formatXAxisTick = (value: any, index: number) => {
    const dataPoint = chartData[index];
    if (!dataPoint) return '';
    // Show date at midnight (new day boundary)
    if (dataPoint.isNewDay) {
      return dataPoint.date;
    }
    // Format time consistently: 4:00am, 11:00pm
    const hour = dataPoint.hour;
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'am' : 'pm';
    return `${hour12}:00${period}`;
  };
  // Custom tick component - show date only when it changes, otherwise show time
  const CustomXAxisTick = (props: any) => {
    const {
      x,
      y,
      payload
    } = props;
    const dataPoint = chartData.find(d => d.time === payload.value);
    if (!dataPoint) return null;
    // Check if this is a new date compared to previous data point
    let showDate = false;
    const currentIndex = chartData.findIndex(d => d.time === payload.value);
    if (currentIndex === 0) {
      // Always show date for first tick
      showDate = true;
    } else if (dataPoint.isNewDay) {
      // Show date at midnight (new day boundary)
      showDate = true;
    }
    return <g transform={`translate(${x},${y})`} data-id="element-57">
        <text x={0} y={0} dy={16} textAnchor="middle" fill={showDate ? '#334155' : '#64748b'} fontSize={10} fontWeight={showDate ? 600 : 500} fontFamily="monospace" data-id="element-58">
          {showDate ? dataPoint.date : dataPoint.time}
        </text>
      </g>;
  };
  const handleExport = () => {
    alert('Exporting chart as PNG...');
  };
  const handleAddToDashboard = (dashboardId?: string) => {
    const newChart: SavedChart = {
      id: `chart-${Date.now()}`,
      region: activeRegion,
      zone: activeZone,
      variable: activeVariable,
      timestamp: Date.now()
    };
    let updatedDashboards = [...dashboards];
    if (dashboardId) {
      // Add to existing dashboard
      updatedDashboards = updatedDashboards.map(d => {
        if (d.id === dashboardId) {
          return {
            ...d,
            charts: [...d.charts, newChart],
            updatedAt: Date.now()
          };
        }
        return d;
      });
    } else {
      // Create new dashboard
      const title = newDashboardTitle.trim() || `Dashboard ${new Date().toLocaleDateString()}`;
      const newDashboard: Dashboard = {
        id: `dash-${Date.now()}`,
        title,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        charts: [newChart]
      };
      updatedDashboards.push(newDashboard);
    }
    localStorage.setItem('jua-dashboards', JSON.stringify(updatedDashboards));
    setDashboards(updatedDashboards);
    // Dispatch event for other components
    window.dispatchEvent(new Event('dashboard-update'));
    // Show success state
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setShowDashboardMenu(false);
      setNewDashboardTitle('');
      setIsCreatingDashboard(false);
    }, 1500);
  };
  const handleLegendClick = (dataKey: string) => {
    setHiddenSeries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey)) {
        newSet.delete(dataKey);
      } else {
        newSet.add(dataKey);
      }
      return newSet;
    });
  };
  // Custom legend component to filter out lowerBound
  const CustomLegend = (props: any) => {
    const {
      payload
    } = props;
    if (!payload) return null;
    // Filter out lowerBound entries
    const filteredPayload = payload.filter((entry: any) => entry.dataKey !== 'lowerBound');
    return <div className="flex items-center justify-center gap-6 mb-4" data-id="element-59">
        {filteredPayload.map((entry: any) => {
        const isHidden = hiddenSeries.has(entry.dataKey);
        return <button key={entry.dataKey} onClick={() => handleLegendClick(entry.dataKey)} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-60">
              <div className="w-3 h-3 rounded-full" style={{
            backgroundColor: entry.color,
            opacity: isHidden ? 0.3 : 1
          }} data-id="element-61" />
              <span className="text-[13px] font-medium" style={{
            color: isHidden ? '#cbd5e1' : '#475569',
            textDecoration: isHidden ? 'line-through' : 'none',
            opacity: isHidden ? 0.5 : 1
          }} data-id="element-62">
                {entry.value}
              </span>
            </button>;
      })}
      </div>;
  };
  if (loading) {
    return <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 flex items-center justify-center h-full shadow-sm shadow-slate-200/50" data-id="element-63">
        <div className="flex flex-col items-center gap-3" data-id="element-64">
          <div className="relative" data-id="element-65">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-pink-500/20 border-t-pink-500" data-id="element-66" />
          </div>
          <p className="text-sm text-slate-600" data-id="element-67">Loading forecast data...</p>
        </div>
      </div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: 0.1
  }} className={`bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 flex flex-col shadow-sm shadow-slate-200/50 relative overflow-hidden ${isFullScreen ? 'fixed inset-4 z-50' : 'h-full'}`} data-id="element-68">
      {isFullScreen && <div className="absolute inset-0 bg-white/95 backdrop-blur-xl -z-10 rounded-xl" data-id="element-69" />}

      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" data-id="element-70" />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 relative z-10" data-id="element-71">
        <div className="flex-1 min-w-0" data-id="element-72">
          <h3 className="text-lg font-semibold text-slate-900 truncate" data-id="element-73">
            {config.title} - {activeZone?.name || activeRegion}
          </h3>
          <p className="text-xs text-slate-500 mt-1" data-id="element-74">
            Real data from Open-Meteo API • EC-IFS, GFS, and simulated EPT-2
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-id="element-75">
          <div className="flex bg-slate-100/80 rounded-lg p-0.5 border border-slate-200/50" data-id="element-76">
            <button onClick={() => setMode('Data')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${mode === 'Data' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-77">
              Data
            </button>
            <button onClick={() => setMode('Delta')} className={`px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${mode === 'Delta' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-78">
              Δ Delta
            </button>
          </div>

          <div className="hidden sm:block w-px h-4 bg-slate-200 mx-1" data-id="element-79" />

          {/* Add to Dashboard Button */}
          <div className="relative" data-id="element-80">
            <button onClick={() => setShowDashboardMenu(!showDashboardMenu)} className={`p-1.5 rounded-lg transition-colors ${showDashboardMenu ? 'bg-pink-50 text-pink-600' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'}`} title="Add to Dashboard" data-id="element-81">
              <LayoutDashboard size={16} data-id="element-82" />
            </button>

            <AnimatePresence data-id="element-83">
              {showDashboardMenu && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-id="element-84">
                  <div className="p-3 border-b border-slate-100 bg-slate-50/50" data-id="element-85">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-86">
                      Add to Dashboard
                    </h4>
                  </div>

                  {saveSuccess ? <div className="p-8 flex flex-col items-center justify-center text-green-600" data-id="element-87">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mb-2" data-id="element-88">
                        <Plus size={16} data-id="element-89" />
                      </div>
                      <span className="text-sm font-medium" data-id="element-90">Added!</span>
                    </div> : <div className="p-2 max-h-[300px] overflow-y-auto custom-scrollbar" data-id="element-91">
                      {dashboards.length > 0 && !isCreatingDashboard && <>
                          {dashboards.map(dash => <button key={dash.id} onClick={() => handleAddToDashboard(dash.id)} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors truncate" data-id="element-92">
                              {dash.title}
                            </button>)}
                          <div className="h-px bg-slate-100 my-2" data-id="element-93" />
                        </>}

                      {isCreatingDashboard ? <div className="p-2" data-id="element-94">
                          <input type="text" value={newDashboardTitle} onChange={e => setNewDashboardTitle(e.target.value)} placeholder="Dashboard Name" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none" autoFocus data-id="element-95" />
                          <div className="flex gap-2" data-id="element-96">
                            <button onClick={() => handleAddToDashboard()} className="flex-1 bg-pink-500 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-pink-600 transition-colors" data-id="element-97">
                              Create & Add
                            </button>
                            <button onClick={() => setIsCreatingDashboard(false)} className="px-3 bg-slate-100 text-slate-600 text-xs font-medium py-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-id="element-98">
                              Cancel
                            </button>
                          </div>
                        </div> : <button onClick={() => setIsCreatingDashboard(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-pink-600 hover:bg-pink-50 transition-colors font-medium" data-id="element-99">
                          <Plus size={14} data-id="element-100" />
                          Create New Dashboard
                        </button>}
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </div>

          <button className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 rounded-lg transition-colors" data-id="element-101">
            <Settings size={16} data-id="element-102" />
          </button>
          <button onClick={() => setIsFullScreen(!isFullScreen)} className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 rounded-lg transition-colors" data-id="element-103">
            <Maximize2 size={16} data-id="element-104" />
          </button>
          <button onClick={handleExport} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100/80 hover:bg-slate-200/80 text-xs font-medium text-slate-700 transition-colors whitespace-nowrap" data-id="element-105">
            <Download size={14} data-id="element-106" />
            <span className="hidden sm:inline" data-id="element-107">PNG</span>
          </button>
          <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 transition-colors p-1" data-id="element-108">
            <GripVertical size={16} data-id="element-109" />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-[300px] w-full relative z-10 mb-4" data-id="element-110">
        <ResponsiveContainer width="100%" height="100%" data-id="element-111">
          <AreaChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: 0,
          bottom: 0
        }} data-id="element-112">
            <defs data-id="element-113">
              <linearGradient id="colorEpt2" x1="0" y1="0" x2="0" y2="1" data-id="element-114">
                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.2} data-id="element-115" />
                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} data-id="element-116" />
              </linearGradient>
              <linearGradient id="colorEcifs" x1="0" y1="0" x2="0" y2="1" data-id="element-117">
                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} data-id="element-118" />
                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-119" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} strokeWidth={1} data-id="element-120" />
            <XAxis dataKey="time" stroke="#cbd5e1" tick={<CustomXAxisTick data-id="element-122" />} tickLine={false} axisLine={{
            stroke: '#e2e8f0',
            strokeWidth: 1
          }} interval="preserveStartEnd" minTickGap={50} height={60} data-id="element-121" />
            <YAxis stroke="#cbd5e1" tick={{
            fill: '#64748b',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: 'monospace'
          }} tickLine={false} axisLine={{
            stroke: '#e2e8f0',
            strokeWidth: 1
          }} dx={-8} data-id="element-123" />
            <Tooltip content={<CustomTooltip unit={config.unit} data-id="element-125" />} cursor={{
            stroke: '#ff006b',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-124" />
            <Legend content={<CustomLegend data-id="element-127" />} verticalAlign="top" height={36} data-id="element-126" />

            {mode === 'Data' ? <>
                <Area type="monotone" dataKey="upperBound" stroke="none" fill="rgba(100, 116, 139, 0.15)" name="Std Dev" legendType="rect" hide={hiddenSeries.has('upperBound')} data-id="element-128" />
                <Area type="monotone" dataKey="lowerBound" stroke="none" fill="#fff" fillOpacity={0.6} name="" legendType="none" hide={hiddenSeries.has('upperBound')} data-id="element-129" />

                <Area type="monotone" dataKey="mean" stroke="#64748b" strokeWidth={2} strokeDasharray="4 4" fill="transparent" name="Mean" hide={hiddenSeries.has('mean')} data-id="element-130" />
                <Area type="monotone" dataKey="ept2" stroke="#a855f7" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEpt2)" name="EPT-2" animationDuration={2000} hide={hiddenSeries.has('ept2')} data-id="element-131" />
                <Area type="monotone" dataKey="ecifs" stroke="#06b6d4" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEcifs)" name="EC-IFS" animationDuration={2000} animationBegin={200} hide={hiddenSeries.has('ecifs')} data-id="element-132" />
                <Area type="monotone" dataKey="gfs" stroke="#ff006b" strokeWidth={2.5} fill="transparent" name="GFS" animationDuration={2000} animationBegin={400} hide={hiddenSeries.has('gfs')} data-id="element-133" />
              </> : <>
                {/* Delta view - show deviation from mean */}
                <Area type="monotone" dataKey="ept2Delta" stroke="#a855f7" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEpt2)" name="EPT-2 Δ" animationDuration={2000} hide={hiddenSeries.has('ept2Delta')} data-id="element-134" />
                <Area type="monotone" dataKey="ecifsDelta" stroke="#06b6d4" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEcifs)" name="EC-IFS Δ" animationDuration={2000} animationBegin={200} hide={hiddenSeries.has('ecifsDelta')} data-id="element-135" />
                <Area type="monotone" dataKey="gfsDelta" stroke="#ff006b" strokeWidth={2.5} fill="transparent" name="GFS Δ" animationDuration={2000} animationBegin={400} hide={hiddenSeries.has('gfsDelta')} data-id="element-136" />
              </>}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="px-4 relative z-10" data-id="element-137">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2" data-id="element-138">
          <div className="flex items-center gap-1" data-id="element-139">
            <ZoomOut size={12} data-id="element-140" />
            <span data-id="element-141">Now</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-142">
            <span data-id="element-143">+48h</span>
            <ZoomIn size={12} data-id="element-144" />
          </div>
        </div>
        <div className="relative h-1.5 bg-slate-100 rounded-full" data-id="element-145">
          <div className="absolute left-0 top-0 bottom-0 bg-slate-300 rounded-full" style={{
          width: `${zoomLevel}%`
        }} data-id="element-146" />
          <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border border-slate-300 rounded-full shadow-sm cursor-pointer hover:scale-110 transition-transform" style={{
          left: `${zoomLevel}%`
        }} onMouseDown={() => {
          setZoomLevel(Math.max(20, Math.min(100, zoomLevel - 10)));
        }} data-id="element-147" />
        </div>
      </div>

      <div className="absolute left-4 bottom-1/2 -rotate-90 text-[10px] text-slate-500 font-medium tracking-widest origin-left translate-y-1/2 z-10" data-id="element-148">
        {mode === 'Data' ? config.yAxisLabel : `DEVIATION (${config.unit})`}
      </div>

      <div className="relative z-20 mt-3 flex justify-end" data-id="element-149">
        <div className="flex items-center gap-2 text-[10px] text-slate-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-200/50 shadow-sm" data-id="element-150">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-151" />
          Live from Open-Meteo
        </div>
      </div>
    </motion.div>;
}